// Admin pricing matrix page. Single screen that shows every product and option
// in the catalog with their per-square-mile rates across the four canonical
// area buckets. Always-editable cells; saves coalesce per row (see
// pricing-editor-slice). Pricing is also editable inside the catalog board's
// product/option dialogs — this page is the "edit everything in one go" view.
//
// Mobile fallback: under ~1024px the matrix is impractical, so we surface a
// short message pointing admins to the catalog board on small screens. The
// per-product editor on the board works fine on mobile.
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress,
} from '@mui/material';
import { fetchCatalog } from '../../store/admin-catalog-slice.js';
import {
  resetEditor, saveAllDirty, selectSaveStatusCounts,
} from '../../store/pricing-editor-slice.js';
import { buildPricingRows, filterPricingRows } from '../../utils/pricing-matrix.js';
import PricingMatrixToolbar from '../../components/admin/pricing/PricingMatrixToolbar.jsx';
import PricingMatrixTable from '../../components/admin/pricing/PricingMatrixTable.jsx';
import PricingMarginCard from '../../components/admin/pricing/PricingMarginCard.jsx';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard.js';
import { CUSTOM_QUOTE_MIN_SQ_MI } from '../../config/pricing-buckets.js';
import { PRICING_STRATEGIES, STRATEGY_LABELS, STRATEGY_HELP } from '../../config/pricing-strategies.js';

const MIN_VIEWPORT_PX = 1024;

// One section per pricing strategy, shown top-to-bottom. Order is intentional:
// the historical tiered model first, then flat-priced services, then the
// always-manual custom-quote products (read-only here).
const STRATEGY_SECTIONS = [
  PRICING_STRATEGIES.TIERED,
  PRICING_STRATEGIES.FLAT,
  PRICING_STRATEGIES.CUSTOM_QUOTE,
];

const useIsWideEnough = () => {
  const [wide, setWide] = useState(
    () => (typeof window === 'undefined' ? true : window.innerWidth >= MIN_VIEWPORT_PX),
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setWide(window.innerWidth >= MIN_VIEWPORT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return wide;
};

const AdminPricingPage = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.adminCatalog);
  const wide = useIsWideEnough();

  const [search, setSearch] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  useEffect(() => {
    dispatch(fetchCatalog());
    return () => { dispatch(resetEditor()); };
  }, [dispatch]);

  // Build a row list per strategy section. Each section's rows carry their
  // strategy + values so the table and save path stay strategy-aware.
  const sections = useMemo(() => STRATEGY_SECTIONS.map((strategy) => ({
    strategy,
    rows: buildPricingRows({ ...data, strategy }),
  })), [data]);

  // Combined list across all sections — what saveAllDirty resolves dirty rows
  // against. Section row keys are unique across strategies (a target has one
  // strategy), so a flat concat is safe.
  const allRows = useMemo(
    () => sections.flatMap((section) => section.rows),
    [sections],
  );

  const visibleSections = useMemo(
    () => sections.map((section) => ({
      strategy: section.strategy,
      rows: filterPricingRows(section.rows, { search, selectedCategoryIds }),
    })),
    [sections, search, selectedCategoryIds],
  );

  const { dirty: dirtyCount } = useSelector(selectSaveStatusCounts);
  const [marginDirty, setMarginDirty] = useState(false);

  // Guard both the matrix's per-row edits and the margin card against losing
  // work via in-app navigation or a hard unload.
  useUnsavedChangesGuard(dirtyCount > 0 || marginDirty);

  const handleSave = () => dispatch(saveAllDirty(allRows));

  const toggleCategory = (id) => {
    setSelectedCategoryIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  if (!wide) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Pricing
        </Typography>
        <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
          The pricing matrix is designed for wider screens. On a small display, edit
          rates from the Catalog board — open a product or option, and use the
          tiered pricing editor inside its dialog.
        </Alert>
        <PricingMarginCard onDirtyChange={setMarginDirty} />
      </Box>
    );
  }

  if (loading && data.categories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading pricing" />
      </Box>
    );
  }

  const renderResults = () => {
    if (allRows.length === 0) {
      return (
        <Alert severity="info">
          No products yet. Add categories and products from the Catalog board first.
        </Alert>
      );
    }
    const anyVisible = visibleSections.some((section) => section.rows.length > 0);
    if (!anyVisible) {
      return <Alert severity="info">No products match the current filters.</Alert>;
    }
    return visibleSections.map((section) => {
      if (section.rows.length === 0) return null;
      return (
        <Box key={section.strategy} sx={{ mb: 4 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            {STRATEGY_LABELS[section.strategy]}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {STRATEGY_HELP[section.strategy]}
          </Typography>
          <PricingMatrixTable rows={section.rows} strategy={section.strategy} />
        </Box>
      );
    });
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Pricing
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`Per-square-mile rates for each product and option, across four area buckets. Areas under ${CUSTOM_QUOTE_MIN_SQ_MI} sq mi are quoted manually.`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <PricingMarginCard onDirtyChange={setMarginDirty} />

      <PricingMatrixToolbar
        search={search}
        onSearchChange={setSearch}
        categories={data.categories}
        selectedCategoryIds={selectedCategoryIds}
        onToggleCategory={toggleCategory}
        onSave={handleSave}
      />

      {renderResults()}
    </Box>
  );
};

export default AdminPricingPage;
